import { ROUTE_BASE_NAME } from '~/constants'
import type { GisPlaceDetails } from '~/types/gis-api-aliases'

const POI_ROUTE_BASE_NAMES = new Set<string>([
  ROUTE_BASE_NAME.rv_rentals_destination,
  ROUTE_BASE_NAME.rv_rentals_park,
  ROUTE_BASE_NAME.rv_rentals_event,
  ROUTE_BASE_NAME.rv_rentals_campground,
])

/**
 * Derives the applied search from the URL on every navigation, so that the URL stays
 * the only source of truth for what is being searched.
 */
export default defineNuxtRouteMiddleware(async (to) => {
  const getRouteBaseName = useRouteBaseName()
  const toRouteBaseName = getRouteBaseName(to)?.toString() ?? ''
  const isPoiRoute = POI_ROUTE_BASE_NAMES.has(toRouteBaseName)

  // Skip the middleware for RV rental routes
  if (
    toRouteBaseName === ROUTE_BASE_NAME.rv_rental
    || toRouteBaseName === ROUTE_BASE_NAME.rv_rental_request_to_book_alias
    || toRouteBaseName.startsWith(ROUTE_BASE_NAME.request_to_book)
  ) {
    return
  }

  const { $captureError, $gis, $i18n } = useNuxtApp()
  const searchData = useSearchData()

  const parsedSearchData = parseSearchQuery(to.query)

  /**
   * Search geographic priority
   *
   * 1. Geo bounds search
   * 2. PlaceId search
   * 3. SearchAddress search
   * 4. Geolocation search, which needs nothing from the URL
   *
   * This is the one part of the search the URL cannot answer on its own, since a place
   * has to be looked up.
   */
  async function resolveLocation(): Promise<GisPlaceDetails | undefined> {
    if (parsedSearchData.bounds) {
      return undefined
    }

    const placeId = getQueryParameter(to.query, 'PlaceId')
    const searchAddress = getQueryParameter(to.query, 'SearchAddress')
    const appliedLocation = searchData.value.location
    const language = $i18n.locale.value === 'fr' ? 'Fr' : 'En'

    try {
      if (placeId) {
        return appliedLocation?.placeId === placeId
          ? appliedLocation
          : await $gis('/Place/Details', { query: { PlaceId: placeId, Language: language } })
      }

      if (searchAddress) {
        return appliedLocation ?? await $gis('/Geocoding/Geocode', { query: { Address: searchAddress, Language: language } })
      }
    }
    catch (error) {
      // A GIS outage degrades to a location-less search rather than failing the navigation.
      $captureError(error)
    }

    return undefined
  }

  const resolvedLocation = await resolveLocation()

  searchData.value = {
    ...parsedSearchData,
    location: resolvedLocation ?? (isPoiRoute ? searchData.value.location : undefined),
  }
})
